import { useCallback, useEffect, useMemo, useState } from 'react';
import { MecProduct } from '@adapters/bigcommerce/types/product';
import { usePdp } from '@context/PdpContext';
import { MerchandisingSelectionConfig } from '_components/Product/SizeSelection/types';

const compareMerchSizeValues = (a: string, b: string): number => {
  const na = Number(a);
  const nb = Number(b);
  if (
    !Number.isNaN(na) &&
    !Number.isNaN(nb) &&
    a.trim() !== '' &&
    b.trim() !== ''
  )
    return na - nb;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
};

const PRIMARY_SIZE_DROPDOWN_THRESHOLD = 12;
const EMPTY_AXIS_VALUE = '';

export const useMerchandisingSizeSelection = (
  merch: MecProduct['sizeSelectionMerchandising']
): MerchandisingSelectionConfig | undefined => {
  const { selectedSizeId, sizes, setSelectedSizeId, setAddToCartClicked } =
    usePdp();

  const availableCombos = useMemo(() => {
    if (!merch) return [];
    const palette = (sizes ?? []).filter((s) => s.isColourSelected);
    const idOk = new Set(
      palette.filter((s) => !s.notAvailableForSelectedColour).map((s) => s.id)
    );
    return merch.combinations.filter((c) => idOk.has(c.sizeId));
  }, [merch, sizes]);

  const axisConfig = useMemo(() => {
    if (!merch) {
      return {
        primaryField: 'primaryValue' as const,
        secondaryField: 'secondaryValue' as const,
        axisPrimaryLabel: '',
        axisSecondaryLabel: '',
      };
    }

    const primaryUniqueCount = new Set(
      merch.combinations.map((combo) => combo.primaryValue)
    ).size;
    const secondaryNonEmptyValues = merch.combinations
      .map((combo) => combo.secondaryValue)
      .filter((value) => value.trim() !== EMPTY_AXIS_VALUE);
    const secondaryUniqueCount = new Set(secondaryNonEmptyValues).size;

    const useSwappedAxes =
      secondaryUniqueCount > 0 && secondaryUniqueCount < primaryUniqueCount;

    return {
      primaryField: useSwappedAxes
        ? ('secondaryValue' as const)
        : ('primaryValue' as const),
      secondaryField: useSwappedAxes
        ? ('primaryValue' as const)
        : ('secondaryValue' as const),
      axisPrimaryLabel: useSwappedAxes
        ? merch.axisSecondary
        : merch.axisPrimary,
      axisSecondaryLabel: useSwappedAxes
        ? merch.axisPrimary
        : merch.axisSecondary,
    };
  }, [merch]);

  const primaryValues = useMemo(() => {
    if (!merch) return [];
    const unique = [
      ...new Set(
        merch.combinations.map((combo) => combo[axisConfig.primaryField])
      ),
    ];
    return unique.sort(compareMerchSizeValues);
  }, [merch, axisConfig.primaryField]);

  const hasSecondaryAxis = useMemo(() => {
    if (!merch) return false;
    return merch.combinations.some(
      (combo) => combo[axisConfig.secondaryField].trim() !== EMPTY_AXIS_VALUE
    );
  }, [merch, axisConfig.secondaryField]);

  const disabledPrimaryValues = useMemo(() => {
    if (!primaryValues.length) return [];
    const enabled = new Set(
      availableCombos.map((combo) => combo[axisConfig.primaryField])
    );
    return primaryValues.filter((value) => !enabled.has(value));
  }, [availableCombos, primaryValues, axisConfig.primaryField]);

  const primaryUsesDropdown =
    primaryValues.length > PRIMARY_SIZE_DROPDOWN_THRESHOLD;

  const [merchPrimary, setMerchPrimary] = useState<string | null>(null);
  const [merchSecondary, setMerchSecondary] = useState<string | null>(null);

  const secondaryValues = useMemo(() => {
    if (!merch || merchPrimary == null) return [];
    const unique = [
      ...new Set(
        merch.combinations
          .filter((combo) => combo[axisConfig.primaryField] === merchPrimary)
          .map((combo) => combo[axisConfig.secondaryField])
      ),
    ];
    return unique.sort(compareMerchSizeValues);
  }, [merch, merchPrimary, axisConfig.primaryField, axisConfig.secondaryField]);

  const disabledSecondaryValues = useMemo(() => {
    if (merchPrimary == null || !secondaryValues.length) return [];
    const enabled = new Set(
      availableCombos
        .filter((combo) => combo[axisConfig.primaryField] === merchPrimary)
        .map((combo) => combo[axisConfig.secondaryField])
    );
    return secondaryValues.filter((value) => !enabled.has(value));
  }, [
    availableCombos,
    merchPrimary,
    secondaryValues,
    axisConfig.primaryField,
    axisConfig.secondaryField,
  ]);

  useEffect(() => {
    if (!merch) return;
    if (!primaryValues.length) {
      setMerchPrimary(null);
      setMerchSecondary(null);
      if (selectedSizeId !== null) setSelectedSizeId(null);
      return;
    }

    const availablePrimaryValues = [
      ...new Set(
        availableCombos.map((combo) => combo[axisConfig.primaryField])
      ),
    ].sort(compareMerchSizeValues);

    setMerchPrimary((prev) => {
      if (prev != null && availablePrimaryValues.includes(prev)) return prev;
      if (availablePrimaryValues.length === 0) return null;
      if (
        hasSecondaryAxis &&
        primaryUsesDropdown &&
        availablePrimaryValues.length > 1
      )
        return null;
      return availablePrimaryValues[0];
    });
  }, [
    merch,
    primaryValues,
    availableCombos,
    hasSecondaryAxis,
    primaryUsesDropdown,
    selectedSizeId,
    setSelectedSizeId,
    axisConfig.primaryField,
  ]);

  useEffect(() => {
    if (!merch) return;
    if (merchPrimary == null) {
      setMerchSecondary(null);
      return;
    }
    const availableSecondaryValues = [
      ...new Set(
        availableCombos
          .filter((combo) => combo[axisConfig.primaryField] === merchPrimary)
          .map((combo) => combo[axisConfig.secondaryField])
      ),
    ].sort(compareMerchSizeValues);

    setMerchSecondary((prev) => {
      if (availableSecondaryValues.length === 0) return null;
      if (hasSecondaryAxis && primaryUsesDropdown) {
        if (prev != null && availableSecondaryValues.includes(prev))
          return prev;
        return availableSecondaryValues[0];
      }
      if (availableSecondaryValues.length === 1) {
        return availableSecondaryValues[0];
      }
      if (prev != null && availableSecondaryValues.includes(prev)) return prev;
      return null;
    });
  }, [
    merch,
    merchPrimary,
    availableCombos,
    hasSecondaryAxis,
    primaryUsesDropdown,
    axisConfig.primaryField,
    axisConfig.secondaryField,
  ]);

  useEffect(() => {
    if (!merch) return;
    const nextSizeId =
      merchPrimary == null || merchSecondary == null
        ? null
        : availableCombos.find(
            (combo) =>
              combo[axisConfig.primaryField] === merchPrimary &&
              combo[axisConfig.secondaryField] === merchSecondary
          )?.sizeId ?? null;

    if (nextSizeId === selectedSizeId) return;

    if (merchPrimary == null || merchSecondary == null) {
      setSelectedSizeId(null);
      return;
    }
    setSelectedSizeId(nextSizeId);
  }, [
    merch,
    merchPrimary,
    merchSecondary,
    availableCombos,
    selectedSizeId,
    setSelectedSizeId,
    axisConfig.primaryField,
    axisConfig.secondaryField,
  ]);

  const onMerchPrimaryChange = useCallback(
    (value: string | null) => {
      setMerchPrimary(value);
      setMerchSecondary(null);
      setSelectedSizeId(null);
      setAddToCartClicked(false);
    },
    [setSelectedSizeId, setAddToCartClicked]
  );

  const onMerchSecondaryChange = useCallback(
    (value: string | null) => {
      setMerchSecondary(value);
      if (value == null) setSelectedSizeId(null);
      setAddToCartClicked(false);
    },
    [setSelectedSizeId, setAddToCartClicked]
  );

  if (!merch || primaryValues.length === 0) return undefined;

  return {
    axisPrimaryLabel: axisConfig.axisPrimaryLabel,
    axisSecondaryLabel: axisConfig.axisSecondaryLabel,
    primaryValues,
    secondaryValues,
    disabledPrimaryValues,
    disabledSecondaryValues,
    selectedPrimary: merchPrimary,
    selectedSecondary: merchSecondary,
    onPrimaryChange: onMerchPrimaryChange,
    onSecondaryChange: onMerchSecondaryChange,
  };
};
